import { useState } from "react";
import { Chip, Grid, Typography } from "@mui/material";
import ContentBox from "../ContentBox/ContentBox.tsx";
import ButtonLoading from "../Button/ButtonLoading.tsx";
import LegalbirdIoConfirm from "../Modal/LegalbirdIoConfirm.tsx";
import ExecutionJobStatusChip from "./ExecutionJobStatusChip.tsx";
import { ExecutionJob } from "../../types/ExecutionJob/ExecutionJob.ts";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { getCaseLink } from "../../services/Product/ProductService/productService.ts";
import { useCase } from "../../provider/CaseProvider.tsx";
import Button from "@mui/material/Button";
import { apiPost } from "../../services/Api/apiCall.ts";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../services/ReactQuery/reactQueryService.ts";

type ExecutionJobDetailsCardProps = {
  executionJob: ExecutionJob;
};

const ExecutionJobDetailsCard = ({ executionJob }: ExecutionJobDetailsCardProps) => {
  const [rejectConfirmationOpen, setRejectConfirmationOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { product } = useCase();
  const queryClient = useQueryClient();

  if (!product) {
    return null;
  }

  const executeJob = async () => {
    setIsLoading(true);
    await apiPost(`/execution_jobs/${executionJob.id}/execute`);
    await queryClient.invalidateQueries({ queryKey: queryKeys.item("execution_jobs", executionJob.id) });
    setIsLoading(false);
  };

  const rejectJob = async () => {
    setIsLoading(true);
    await apiPost(`/execution_jobs/${executionJob.id}/reject`);
    await queryClient.invalidateQueries({ queryKey: queryKeys.item("execution_jobs", executionJob.id) });
    setIsLoading(false);
  };

  return (
    <ContentBox headline={"Details"}>
      <Grid container spacing={2}>
        <Grid
          size={6}
        >
          <Typography variant={"subtitle2"}>Titel</Typography>
          <Typography>{executionJob.jobTitle || "-"}</Typography>
        </Grid>
        <Grid
          size={{
            xs: 6,
          }}
        >
          <Typography variant={"subtitle2"}>Agent</Typography>
          <Chip label={executionJob.agentType} />
        </Grid>
        <Grid
          size={6}
        >
          <Typography variant={"subtitle2"}>Status</Typography>
          <ExecutionJobStatusChip executionJob={executionJob} />
        </Grid>
        <Grid
          size={6}
        >
          <Typography variant={"subtitle2"}>Freigabe nötig</Typography>
          <Typography>{executionJob.requiresApproval ? "Ja" : "Nein"}</Typography>
        </Grid>
        <Grid size={12}>
          <Typography variant={"subtitle2"}>Beschreibung</Typography>
          <Typography>{executionJob.jobDescription || "-"}</Typography>
        </Grid>
        <Grid
          size={6}
        >
          <Typography variant={"subtitle2"}>Erstellt</Typography>
          <Typography>{dayjs(executionJob.created).format("DD.MM.YYYY HH:mm")}</Typography>
        </Grid>
        <Grid
          size={6}
        >
          <Typography variant={"subtitle2"}>Aktualisiert</Typography>
          <Typography>{dayjs(executionJob.updated).format("DD.MM.YYYY HH:mm")}</Typography>
        </Grid>
        <Grid
          size={6}
        >
          <Button component={Link} to={getCaseLink(product) + "/execution-jobs"}>
            Zur Job-Übersicht
          </Button>
        </Grid>
        <Grid
          size={6}
        >
          <ButtonLoading
            variant={"contained"}
            onClick={executeJob}
            isLoading={isLoading}
            disabled={executionJob.status !== "pendingApproval" || isLoading}
            fullWidth
            sx={{ marginBottom: "1rem" }}
          >
            Job ausführen
          </ButtonLoading>
          <ButtonLoading
            variant={"outlined"}
            color={"error"}
            onClick={() => setRejectConfirmationOpen(true)}
            isLoading={isLoading}
            disabled={executionJob.status !== "pendingApproval" || isLoading}
            fullWidth
          >
            Job ablehnen
          </ButtonLoading>
        </Grid>
      </Grid>
      <LegalbirdIoConfirm
        open={rejectConfirmationOpen}
        handleClose={() => setRejectConfirmationOpen(false)}
        headline={"Job ablehnen"}
        content={"Soll dieser Job wirklich abgelehnt werden? Dieser Vorgang kann nicht rückgängig gemacht werden."}
        confirmText={"Ablehnen"}
        handleConfirm={rejectJob}
      />
    </ContentBox>
  );
};

export default ExecutionJobDetailsCard;
